import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Sparkles, ShieldCheck } from 'lucide-react';

const AuthPage = ({ setUser }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2fa, setRequires2fa] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const data = isLogin
        ? { email, password, ...(requires2fa && totpCode ? { totp_code: totpCode } : {}) }
        : { email, password, name };

      const response = await axios.post(`${API_URL}${endpoint}`, data);

      if (response.data.requires_2fa) {
        setRequires2fa(true);
        setLoading(false);
        return;
      }

      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRequires2fa(false);
    setTotpCode('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-violet-600 flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <span className="text-white text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>DocuBot AI</span>
          </div>

          <div>
            <h1 className="text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              Your Knowledge Base,
              <br />
              <span className="text-violet-400">AI-Powered</span>
            </h1>
            <p className="text-zinc-400 text-lg mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              Upload documents, create knowledge bases, and chat with an AI assistant that knows your content inside out.
            </p>
            <div className="flex gap-6">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-violet-400" />
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">AI Chat Assistant</div>
                  <div className="text-zinc-500 text-sm">Get instant answers from your docs</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-zinc-600 text-sm">
            &copy; 2025 DocuBot AI. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="auth-heading">
              {requires2fa ? 'Two-Factor Authentication' : (isLogin ? 'Welcome back' : 'Create account')}
            </h2>
            <p className="text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              {requires2fa
                ? 'Enter the 6-digit code from your authenticator app'
                : (isLogin ? 'Sign in to your account' : 'Start building your knowledge base')}
            </p>
          </div>

          {requires2fa ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-2xl bg-violet-100 flex items-center justify-center">
                  <ShieldCheck className="h-8 w-8 text-violet-600" />
                </div>
              </div>
              <div>
                <Label htmlFor="totp">Verification Code</Label>
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  className="mt-2 text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="totp-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 transition-all duration-200"
                disabled={loading || totpCode.length !== 6}
                data-testid="totp-verify-button"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <button
                type="button"
                onClick={resetForm}
                className="w-full text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
                data-testid="back-to-login-button"
              >
                Back to login
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                {!isLogin && (
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      className="mt-2"
                      data-testid="name-input"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 transition-all duration-200"
                  disabled={loading}
                  data-testid="auth-submit-button"
                >
                  {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Create Account')}
                </Button>
              </form>

              {isLogin && (
                <div className="mt-4 text-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-violet-600 hover:text-violet-700 font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}

              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-all duration-200"
                  data-testid="toggle-auth-mode"
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
